/**
 * ChapterSelector Component
 *
 * Reusable component for selecting course chapters.
 * Used in partial reset flow to allow students to choose specific chapters.
 *
 * Features:
 * - Multi-select checkbox list
 * - Select all / Clear all actions
 * - Loading and error states
 * - Graded content indicators
 * - Hebrew RTL support
 * - Accessible design
 *
 * @version 1.0.0
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Form,
  Button,
  Spinner,
  Alert,
} from '@edx/paragon';

import messages from '../messages';
import './ChapterSelector.css';

// ===== Sub-components =====

/**
 * Individual chapter checkbox item
 */
const ChapterItem = ({
  chapter,
  isSelected,
  onChange,
  disabled,
  intl,
}) => {
  const handleChange = useCallback(() => {
    onChange(chapter.id);
  }, [chapter.id, onChange]);

  return (
    <div
      className={`chapter-item ${isSelected ? 'chapter-item--selected' : ''}`}
      data-testid={`chapter-item-${chapter.id}`}
    >
      <Form.Checkbox
        checked={isSelected}
        onChange={handleChange}
        disabled={disabled}
        className="chapter-item__checkbox"
      >
        <div className="chapter-item__content">
          <span className="chapter-item__name">
            {chapter.displayName || chapter.display_name}
          </span>
          <div className="chapter-item__meta">
            <span className="chapter-item__subsections">
              {intl.formatMessage(messages.chapterSelectorSubsections, {
                count: chapter.subsectionsCount || chapter.subsections_count,
              })}
            </span>
            {(chapter.hasGradedContent || chapter.has_graded_content) && (
              <span className="chapter-item__graded-badge">
                {intl.formatMessage(messages.chapterSelectorGradedBadge)}
              </span>
            )}
          </div>
        </div>
      </Form.Checkbox>
    </div>
  );
};

ChapterItem.propTypes = {
  chapter: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    display_name: PropTypes.string,
    subsectionsCount: PropTypes.number,
    subsections_count: PropTypes.number,
    hasGradedContent: PropTypes.bool,
    has_graded_content: PropTypes.bool,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

ChapterItem.defaultProps = {
  disabled: false,
};

// ===== Main Component =====

/**
 * ChapterSelector component
 *
 * @param {Object} props - Component props
 * @param {Array} props.chapters - List of chapters to display
 * @param {Array} props.selectedChapterIds - Currently selected chapter IDs
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {boolean} props.isLoading - Whether chapters are loading
 * @param {string} props.error - Error message to display
 * @param {boolean} props.disabled - Whether selection is disabled
 * @param {string} props.title - Section title
 * @param {boolean} props.showSelectAll - Whether to show select all / clear all buttons
 */
const ChapterSelector = ({
  chapters,
  selectedChapterIds,
  onSelectionChange,
  isLoading,
  error,
  disabled,
  title,
  showSelectAll,
}) => {
  const intl = useIntl();
  // ===== Computed Values =====

  const allChapterIds = useMemo(
    () => chapters.map((ch) => ch.id),
    [chapters],
  );

  const allSelected = useMemo(
    () => chapters.length > 0 && selectedChapterIds.length === chapters.length,
    [chapters, selectedChapterIds],
  );

  const noneSelected = useMemo(
    () => selectedChapterIds.length === 0,
    [selectedChapterIds],
  );

  const selectionCount = selectedChapterIds.length;

  // ===== Handlers =====

  /**
   * Toggle single chapter selection
   */
  const handleChapterToggle = useCallback((chapterId) => {
    const newSelection = selectedChapterIds.includes(chapterId)
      ? selectedChapterIds.filter((id) => id !== chapterId)
      : [...selectedChapterIds, chapterId];

    onSelectionChange(newSelection);
  }, [selectedChapterIds, onSelectionChange]);

  /**
   * Select all chapters
   */
  const handleSelectAll = useCallback(() => {
    onSelectionChange(allChapterIds);
  }, [allChapterIds, onSelectionChange]);

  /**
   * Clear all selections
   */
  const handleClearAll = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  // ===== Render: Loading State =====

  if (isLoading) {
    return (
      <div className="chapter-selector chapter-selector--loading">
        <div className="chapter-selector__loading">
          <Spinner animation="border" size="sm" />
          <span>{intl.formatMessage(messages.chapterSelectorLoading)}</span>
        </div>
      </div>
    );
  }

  // ===== Render: Error State =====

  if (error) {
    return (
      <div className="chapter-selector chapter-selector--error">
        <Alert variant="danger">
          <Alert.Heading>{intl.formatMessage(messages.chapterSelectorError)}</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  // ===== Render: Empty State =====

  if (!chapters || chapters.length === 0) {
    return (
      <div className="chapter-selector chapter-selector--empty">
        <Alert variant="warning">
          {intl.formatMessage(messages.chapterSelectorEmpty)}
        </Alert>
      </div>
    );
  }

  // ===== Render: Normal State =====

  return (
    <div className="chapter-selector" data-testid="chapter-selector">
      {/* Header */}
      <div className="chapter-selector__header">
        {title && (
          <h5 className="chapter-selector__title">
            {title}
          </h5>
        )}

        {showSelectAll && (
          <div className="chapter-selector__actions">
            <Button
              variant="link"
              size="sm"
              onClick={handleSelectAll}
              disabled={disabled || allSelected}
              className="chapter-selector__action-btn"
            >
              {intl.formatMessage(messages.chapterSelectorSelectAll)}
            </Button>
            <span className="chapter-selector__action-divider">|</span>
            <Button
              variant="link"
              size="sm"
              onClick={handleClearAll}
              disabled={disabled || noneSelected}
              className="chapter-selector__action-btn"
            >
              {intl.formatMessage(messages.chapterSelectorClearAll)}
            </Button>
          </div>
        )}
      </div>

      {/* Chapters List */}
      <div
        className="chapter-selector__list"
        role="group"
        aria-label={intl.formatMessage(messages.chapterSelectorAriaLabel)}
      >
        {chapters.map((chapter) => (
          <ChapterItem
            key={chapter.id}
            chapter={chapter}
            isSelected={selectedChapterIds.includes(chapter.id)}
            onChange={handleChapterToggle}
            disabled={disabled}
            intl={intl}
          />
        ))}
      </div>

      {/* Selection Summary */}
      {selectionCount > 0 && (
        <div className="chapter-selector__summary">
          <span className="chapter-selector__summary-text">
            {intl.formatMessage(messages.chapterSelectorSummary, { count: selectionCount })}
          </span>
        </div>
      )}
    </div>
  );
};

ChapterSelector.propTypes = {
  /** List of chapters to display */
  chapters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    display_name: PropTypes.string,
    subsectionsCount: PropTypes.number,
    subsections_count: PropTypes.number,
    hasGradedContent: PropTypes.bool,
    has_graded_content: PropTypes.bool,
  })),
  /** Array of selected chapter IDs */
  selectedChapterIds: PropTypes.arrayOf(PropTypes.string),
  /** Callback when selection changes - receives array of selected IDs */
  onSelectionChange: PropTypes.func.isRequired,
  /** Whether chapters are loading */
  isLoading: PropTypes.bool,
  /** Error message to display */
  error: PropTypes.string,
  /** Whether selection is disabled */
  disabled: PropTypes.bool,
  /** Section title */
  title: PropTypes.string,
  /** Whether to show select all / clear all buttons */
  showSelectAll: PropTypes.bool,
};

ChapterSelector.defaultProps = {
  chapters: [],
  selectedChapterIds: [],
  isLoading: false,
  error: null,
  disabled: false,
  title: 'Select chapters to reset',
  showSelectAll: true,
};

export default ChapterSelector;
